import type { ErrorInfo } from 'react-dom/client';

/** Browser-side reporting seam. Native Error objects retain stacks and causes in developer tools. */
export function reportBrowserError(context: string, error: unknown, detail?: unknown): void {
  console.error(`[${context}]`, error, ...(detail === undefined ? [] : [detail]));
}

/** Opaque server-side support reference carried by tRPC errors, when one is available. */
export function browserErrorReference(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  try {
    const directErrorId = Reflect.get(error, 'errorId');
    if (typeof directErrorId === 'string' && directErrorId.length > 0) return directErrorId;
    const data = Reflect.get(error, 'data');
    if (typeof data !== 'object' || data === null) return undefined;
    const errorId = Reflect.get(data, 'errorId');
    return typeof errorId === 'string' && errorId.length > 0 ? errorId : undefined;
  } catch {
    return undefined;
  }
}

/** Install once, before React starts, so non-React failures are not reduced to browser strings. */
export function installGlobalErrorReporting(): void {
  window.addEventListener('error', (event) => {
    reportBrowserError('window.error', event.error ?? new Error(event.message));
  });
  window.addEventListener('unhandledrejection', (event) => {
    reportBrowserError('window.unhandledrejection', event.reason);
  });
}

export function reportReactError(context: string, error: unknown, errorInfo: ErrorInfo): void {
  reportBrowserError(context, error, { componentStack: errorInfo.componentStack });
}
